import React, { useEffect } from 'react';
import { Input } from "../ui/input"; // Assuming Input is in ui/components/ui/input
import { Button } from "../ui/button"; // Assuming Button is in ui/components/ui/button

/**
 * Renders the chat input field and send button.
 * 
 * @param {object} props - Component props.
 * @param {string} props.inputMessage - The current value of the input field.
 * @param {function} props.setInputMessage - Function to update the input field value.
 * @param {function} props.handleSendMessage - Function to call when the send button is clicked or Enter is pressed.
 * @param {boolean} props.isProcessing - Boolean indicating if a message is currently being sent/processed.
 * @param {React.RefObject} props.inputRef - Ref to attach to the input element for focusing.
 */
export default function ChatInput({ 
    inputMessage, 
    setInputMessage, 
    handleSendMessage, 
    isProcessing, 
    inputRef 
}) {
    // Handle Enter key press in the input field
    const handleKeyPress = async (e) => { // Make async
        if (e.key === 'Enter' && !e.shiftKey) { // Send on Enter, allow Shift+Enter for newline
            e.preventDefault(); // Prevent default form submission or newline insertion
            if (!isProcessing && inputMessage.trim()) { // Check if processing or empty
                await handleSendMessage(); // Wait for send to complete
            }
        }
    };

    // Ensure input stays focused after sending
    useEffect(() => {
        if (!isProcessing && inputRef?.current) {
            inputRef.current.focus();
        }
    }, [isProcessing, inputRef]);

    return (
        <div className="p-2 border-t">
            <div className="flex space-x-3">
                <Input
                    ref={inputRef}
                    type="text" // Consider using <Textarea> if multi-line input is desired
                    value={inputMessage}
                    onChange={(e) => setInputMessage(e.target.value)}
                    onKeyDown={handleKeyPress} // Changed from onKeyPress to onKeyDown for better compatibility
                    className="flex-1"
                    placeholder="Type your message..."
                    disabled={isProcessing} // Disable input while processing
                    data-testid="chat-input"
                />
                <Button
                    onClick={async () => { // Make async
                        if (!isProcessing && inputMessage.trim()) { // Check if processing or empty
                            await handleSendMessage(); // Wait for send to complete
                            // No need to manually focus here; handled by useEffect
                        }
                    }}
                    disabled={isProcessing || !inputMessage.trim()} // Disable if processing or input is empty
                    className="bg-blue-500 hover:bg-blue-600 text-white"
                    data-testid="send-button"
                >
                    {isProcessing ? 'Sending...' : 'Send'} {/* Indicate processing state */}
                </Button>
            </div>
        </div>
    );
}